import { useCallback, useEffect, useRef, useState } from 'react';
import { CalendarClock, Pencil, Plus, Trash2, X } from 'lucide-react';
import { apiPersonWorkSchedule } from '../api/client.js';
import WorkDaysEditor, { WORK_DAYS, DEFAULT_WORK_DAYS, weeklyTotal } from './WorkDaysEditor.jsx';

/**
 * Section « Disponibilité » de la fiche Person (mode édition) — gère les
 * versions datées de planning hebdomadaire (`PersonWorkScheduleHistory`).
 *
 * Une version s'applique de sa date d'effet jusqu'à la suivante. « Nouvelle
 * disponibilité » clôt l'actuelle et en ouvre une à la date choisie. Toutes les
 * mutations passent par les endpoints dédiés (sauvegarde immédiate, comme
 * `PersonManagersSection`) — indépendantes du bouton « Enregistrer » de la
 * modale Person.
 *
 * Props :
 *  - personId : id de la Person (requis — la section n'apparaît qu'en édition)
 *  - canEdit  : true pour ADMIN ou MANAGER sur sa descendance
 *  - onCurrentChange(workDaysHours) : notifie la modale parente quand la dispo
 *    en vigueur change (pour resynchroniser un éventuel affichage courant)
 */

/** Convertit un `workDaysHours` (objet OU tableau Float [dim,lun,…]) en objet. */
function normalize(wdh) {
  if (!wdh) return { ...DEFAULT_WORK_DAYS };
  const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);
  if (Array.isArray(wdh)) {
    if (wdh.length !== 7) return { ...DEFAULT_WORK_DAYS };
    return {
      sunday: num(wdh[0]),
      monday: num(wdh[1]),
      tuesday: num(wdh[2]),
      wednesday: num(wdh[3]),
      thursday: num(wdh[4]),
      friday: num(wdh[5]),
      saturday: num(wdh[6]),
    };
  }
  const out = { ...DEFAULT_WORK_DAYS };
  for (const k of Object.keys(DEFAULT_WORK_DAYS)) {
    if (wdh[k] != null && Number.isFinite(Number(wdh[k]))) out[k] = Number(wdh[k]);
  }
  return out;
}

function fmtDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('fr-FR');
}

/** ISO → "YYYY-MM-DD" (UTC) pour pré-remplir un <input type="date">. */
function toDateInput(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString().slice(0, 10);
}

function todayInput() {
  return new Date().toISOString().slice(0, 10);
}

const sectionTitle = {
  margin: '4px 0 6px',
  fontSize: 12,
  fontWeight: 600,
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  color: 'var(--text-muted)',
};

const ghostBtn = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  padding: '6px 12px',
  background: 'transparent',
  color: 'var(--accent)',
  border: '1px dashed var(--glass-border)',
  borderRadius: 'var(--radius-pill)',
  fontSize: 13,
  cursor: 'pointer',
};

const primaryBtn = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  background: 'var(--accent)',
  color: '#fff',
  border: 'none',
  borderRadius: 'var(--radius-pill)',
  padding: '8px 16px',
  fontSize: 13,
  fontWeight: 500,
  cursor: 'pointer',
};

const secondaryBtn = {
  background: 'transparent',
  color: 'var(--text)',
  border: '1px solid var(--glass-border)',
  borderRadius: 'var(--radius-sm)',
  padding: '6px 14px',
  fontSize: 13,
  cursor: 'pointer',
};

const iconBtn = {
  background: 'transparent',
  border: 'none',
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  padding: 4,
};

export default function WorkScheduleSection({ personId, canEdit = false, onCurrentChange }) {
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  // editor: null | { mode: 'create'|'edit', versionId?, effectiveDate, workDaysHours }
  const [editor, setEditor] = useState(null);

  // `onCurrentChange` est souvent une fonction inline (recréée à chaque render
  // du parent). On la garde dans une ref pour NE PAS en dépendre dans `reload`
  // (sinon : reload change à chaque render → effet relancé en boucle → la
  // section reste en « Chargement… » et clignote).
  const onCurrentChangeRef = useRef(onCurrentChange);
  onCurrentChangeRef.current = onCurrentChange;

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { items } = await apiPersonWorkSchedule.list(personId);
      setVersions(items ?? []);
      const current = (items ?? []).find((v) => v.isCurrent);
      if (current && onCurrentChangeRef.current) {
        onCurrentChangeRef.current(normalize(current.workDaysHours));
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [personId]);

  useEffect(() => {
    if (!personId) return;
    reload();
  }, [personId, reload]);

  const openCreate = () => {
    // Pré-remplit avec la dispo courante (ou le défaut) pour faciliter l'ajustement.
    const current = versions.find((v) => v.isCurrent) ?? versions[0];
    setError(null);
    setEditor({
      mode: 'create',
      effectiveDate: todayInput(),
      workDaysHours: current ? normalize(current.workDaysHours) : { ...DEFAULT_WORK_DAYS },
    });
  };

  const openEdit = (v) => {
    setError(null);
    setEditor({
      mode: 'edit',
      versionId: v.id,
      effectiveDate: toDateInput(v.effectiveDate),
      workDaysHours: normalize(v.workDaysHours),
    });
  };

  const onDayChange = (dayKey, raw) => {
    const n = raw === '' ? 0 : Number(raw);
    setEditor((prev) => ({
      ...prev,
      workDaysHours: { ...prev.workDaysHours, [dayKey]: Number.isFinite(n) ? n : 0 },
    }));
  };

  const save = async () => {
    if (!editor.effectiveDate) {
      setError('La date d’effet est requise');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      if (editor.mode === 'create') {
        await apiPersonWorkSchedule.create(personId, {
          effectiveDate: editor.effectiveDate,
          workDaysHours: editor.workDaysHours,
        });
      } else {
        await apiPersonWorkSchedule.update(personId, editor.versionId, {
          effectiveDate: editor.effectiveDate,
          workDaysHours: editor.workDaysHours,
        });
      }
      setEditor(null);
      await reload();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const onDelete = async (v) => {
    if (!window.confirm('Supprimer cette version de disponibilité ?')) return;
    setBusy(true);
    setError(null);
    try {
      await apiPersonWorkSchedule.remove(personId, v.id);
      await reload();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    // minHeight réservé : évite que la modale (centrée verticalement) ne
    // « saute » quand le contenu passe de « Chargement… » à la liste chargée.
    <div style={{ minHeight: 260 }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 8,
        }}
      >
        <div style={{ ...sectionTitle, margin: 0 }}>Disponibilité (heures/jour)</div>
        {canEdit && !editor ? (
          <button type="button" onClick={openCreate} style={ghostBtn}>
            <Plus size={14} /> Nouvelle disponibilité
          </button>
        ) : null}
      </div>

      {error ? (
        <div style={{ color: 'var(--danger)', fontSize: 12, marginBottom: 8 }}>{error}</div>
      ) : null}

      {/* Éditeur inline (création / édition d'une version) */}
      {editor ? (
        <div
          style={{
            padding: 14,
            marginBottom: 14,
            background: 'var(--glass)',
            border: '1px solid var(--accent)',
            borderRadius: 'var(--radius-md)',
          }}
        >
          <div style={{ marginBottom: 12, maxWidth: 220 }}>
            <label
              style={{
                display: 'block',
                marginBottom: 6,
                fontSize: 13,
                color: 'var(--text-muted)',
              }}
            >
              Date d’effet
            </label>
            <input
              type="date"
              value={editor.effectiveDate}
              onChange={(e) => setEditor((prev) => ({ ...prev, effectiveDate: e.target.value }))}
              style={{
                width: '100%',
                padding: '8px 10px',
                background: 'var(--bg)',
                color: 'var(--text)',
                border: '1px solid var(--glass-border)',
                borderRadius: 'var(--radius-sm)',
                fontSize: 14,
              }}
            />
            <div style={{ marginTop: 4, fontSize: 11, color: 'var(--text-muted)' }}>
              {editor.mode === 'create'
                ? 'La disponibilité actuelle est clôturée la veille de cette date.'
                : 'Date à partir de laquelle cette version s’applique.'}
            </div>
          </div>

          <WorkDaysEditor value={editor.workDaysHours} onChange={onDayChange} />

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
            <button type="button" onClick={() => setEditor(null)} style={secondaryBtn} disabled={busy}>
              Annuler
            </button>
            <button
              type="button"
              onClick={save}
              style={{ ...primaryBtn, opacity: busy ? 0.6 : 1 }}
              disabled={busy}
            >
              {editor.mode === 'create' ? 'Créer la version' : 'Enregistrer'}
            </button>
          </div>
        </div>
      ) : null}

      {/* Timeline des versions */}
      {loading ? (
        <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>Chargement…</div>
      ) : versions.length === 0 ? (
        <div style={{ fontSize: 13, color: 'var(--text-muted)', fontStyle: 'italic' }}>
          Aucune disponibilité enregistrée.
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 10 }}>
          {versions.map((v) => {
            const wd = normalize(v.workDaysHours);
            return (
              <div
                key={v.id}
                style={{
                  padding: 12,
                  background: 'var(--card-bg)',
                  border: `1px solid ${v.isCurrent ? 'var(--accent)' : 'var(--glass-border)'}`,
                  borderRadius: 'var(--radius-md)',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 8,
                    marginBottom: 8,
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                    <CalendarClock size={15} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
                    <span style={{ fontSize: 13, color: 'var(--text)' }}>
                      Dès le <strong>{fmtDate(v.effectiveDate)}</strong>
                      {v.effectiveTo ? (
                        <span style={{ color: 'var(--text-muted)' }}>
                          {' '}
                          → {fmtDate(v.effectiveTo)}
                        </span>
                      ) : (
                        <span style={{ color: 'var(--text-muted)' }}> → en cours</span>
                      )}
                    </span>
                    {v.isCurrent ? (
                      <span
                        style={{
                          fontSize: 11,
                          fontWeight: 600,
                          color: 'var(--accent)',
                          background: 'var(--glass)',
                          border: '1px solid var(--accent)',
                          borderRadius: 999,
                          padding: '1px 8px',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        En vigueur
                      </span>
                    ) : null}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 2, flexShrink: 0 }}>
                    <span style={{ fontSize: 12, color: 'var(--text-muted)', marginRight: 6 }}>
                      {weeklyTotal(wd)} h/sem
                    </span>
                    {canEdit ? (
                      <>
                        <button
                          type="button"
                          onClick={() => openEdit(v)}
                          title="Modifier cette version"
                          style={iconBtn}
                          disabled={busy}
                        >
                          <Pencil size={15} style={{ color: 'var(--text-muted)' }} />
                        </button>
                        <button
                          type="button"
                          onClick={() => onDelete(v)}
                          title="Supprimer cette version"
                          style={iconBtn}
                          disabled={busy}
                        >
                          <Trash2 size={15} style={{ color: 'var(--danger)' }} />
                        </button>
                      </>
                    ) : null}
                  </div>
                </div>
                {/* Aperçu compact des 7 jours */}
                <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                  {WORK_DAYS.map((d) => {
                    const val = wd[d.key] ?? 0;
                    return (
                      <span
                        key={d.key}
                        style={{
                          fontSize: 11,
                          color: val ? 'var(--text)' : 'var(--text-muted)',
                          opacity: val ? 1 : 0.5,
                          padding: '2px 7px',
                          background: 'var(--glass)',
                          border: '1px solid var(--glass-border)',
                          borderRadius: 'var(--radius-sm)',
                        }}
                      >
                        {d.label} {val}
                      </span>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* X importé pour cohérence d'API future (fermeture inline) */}
      <span style={{ display: 'none' }}>
        <X size={1} />
      </span>
    </div>
  );
}
